import { useEffect, useState, useMemo, useCallback, useRef } from "react";

const isDatabaseDeletedError = (error) => {
  const errorMessage = error?.message || error;
  return (
    errorMessage &&
    typeof errorMessage === "string" &&
    (errorMessage.includes("Database deleted by request") ||
      errorMessage.includes("deleted by request of the user"))
  );
};

const isDatabaseConnectionLost = (error) => {
  const errorMessage = error?.message || error;
  const errorName = error?.name;

  const messageCheck =
    errorMessage &&
    typeof errorMessage === "string" &&
    (errorMessage.includes("Database connection failed.") ||
      errorMessage.includes("The database connection is closing"));

  // AbortError → Aborted transaction, which can occur due to a connection closure.
  // InvalidStateError → An invalid operation, for example, on a closed connection.
  // UnknownError → May indicate an unexpected connection loss.
  const nameCheck =
    errorName &&
    (errorName === "AbortError" ||
      errorName === "InvalidStateError" ||
      errorName === "UnknownError");

  return messageCheck || nameCheck;
};

const isSecurityContextError = (error) => {
  const errorMessage = error?.message || error;
  const errorName = error?.name;

  const messageCheck =
    errorMessage &&
    typeof errorMessage === "string" &&
    errorMessage.includes(
      "IDBFactory.open() called in an invalid security context"
    );
  const nameCheck = errorName === "SecurityError";

  return messageCheck || nameCheck;
};

const getErrorMessage = (error) =>
  error instanceof Error ? error.message : String(error);

const logIndexedDBEvent = (severity, event, details = {}) => {
  console.log(
    JSON.stringify({
      severity,
      source: "useIndexedDB",
      event,
      ...details,
    })
  );
};

/** Reconnect attempts before a store degrades to React state instead of retrying forever. */
const MAX_CONNECTION_ATTEMPTS = 3;
const CONNECTION_TIMEOUT_MS = 5000;

// Singleton instance for IndexedDB connection
let dbInstance = null;
// The resolved connection backing dbInstance. Keeping this separate from the promise
// lets intentional handoffs avoid treating the old connection's close event as a
// failure of the replacement connection.
let activeDb = null;
// Global flag for fallback mode (only work with react state, iframe and unable indexedDB access friendly)
let fallbackMode = false;
/** Every store requested by any hook, so one upgrade can create them all. */
const knownStoreNames = new Set();

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Registered stores the given connection doesn't have yet. */
const missingStores = (db) =>
  [...knownStoreNames].filter((name) => !db.objectStoreNames.contains(name));

/**
 * Opens the database at its current version.
 * @param {string} dbName
 * @returns {Promise<IDBDatabase>}
 * @throws {Error} On open failure or when the open never settles.
 */
const openAtCurrentVersion = (dbName) => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(dbName);

    const timeout = setTimeout(() => {
      request.onerror = null;
      request.onsuccess = null;
      reject(new Error("The database connection is closing"));
    }, CONNECTION_TIMEOUT_MS);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (db) {
        for (const name of missingStores(db)) {
          db.createObjectStore(name);
        }
      }
    };

    request.onsuccess = (event) => {
      clearTimeout(timeout);
      resolve(event.target.result);
    };

    request.onerror = (event) => {
      clearTimeout(timeout);
      reject(event.target.error);
    };
  });
};

/**
 * Opens the database at an explicit version, creating every registered store it's missing.
 * @param {string} dbName
 * @param {number} version
 * @returns {Promise<IDBDatabase>}
 * @throws {Error} On upgrade failure.
 */
const openAtVersion = (dbName, version) => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(dbName, version);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (db) {
        for (const name of missingStores(db)) {
          db.createObjectStore(name);
        }
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
    // Another tab holding the old version would otherwise leave this pending forever.
    request.onblocked = () =>
      reject(new Error("The database connection is closing"));
  });
};

/**
 * Drops the cached connection so the next operation reconnects lazily.
 * @param {IDBDatabase} db
 */
const attachConnectionHandlers = (db) => {
  activeDb = db;

  const clearActiveConnection = () => {
    if (activeDb === db) {
      activeDb = null;
      dbInstance = null;
    }
  };

  db.onerror = (event) => {
    logIndexedDBEvent("ERROR", "database_operation_error", {
      error: getErrorMessage(event.target.error),
    });
    clearActiveConnection();
  };

  db.onclose = () => {
    logIndexedDBEvent("WARNING", "database_connection_closed");
    clearActiveConnection();
  };

  db.onversionchange = () => {
    logIndexedDBEvent("WARNING", "database_version_changed");
    clearActiveConnection();
    db.close();
  };
};

/**
 * Connects and guarantees every registered store exists, with a bounded retry budget.
 * @param {string} dbName
 * @param {number} [attempt]
 * @returns {Promise<IDBDatabase>}
 * @throws {Error} When the retry budget is exhausted or IndexedDB is unavailable.
 */
const connect = async (dbName, attempt = 1, connectionToClose = null) => {
  try {
    // A later store must upgrade from the live connection, not leave it open while a
    // second connection attempts the version change. close() allows in-flight
    // transactions to finish and then releases the upgrade lock.
    if (connectionToClose) {
      if (activeDb === connectionToClose) {
        activeDb = null;
      }
      connectionToClose.close();
    }

    let db = await openAtCurrentVersion(dbName);

    // One upgrade creates every store any hook asked for. Per-store upgrades would each
    // close the shared connection, aborting the other stores' in-flight transactions.
    if (missingStores(db).length > 0) {
      const nextVersion = db.version + 1;
      db.close();
      db = await openAtVersion(dbName, nextVersion);
    }

    attachConnectionHandlers(db);
    return db;
  } catch (error) {
    if (isSecurityContextError(error)) {
      logIndexedDBEvent("WARNING", "security_context_unavailable", {
        error: getErrorMessage(error),
      });
      fallbackMode = true;
      throw error;
    }

    logIndexedDBEvent("ERROR", "database_connection_failed", {
      attempt,
      error: getErrorMessage(error),
    });

    if (attempt >= MAX_CONNECTION_ATTEMPTS) {
      logIndexedDBEvent("WARNING", "connection_retry_exhausted", {
        attempts: attempt,
      });
      fallbackMode = true;
      throw error;
    }

    await wait(1000 * 2 ** (attempt - 1));
    return connect(dbName, attempt + 1);
  }
};

/**
 * Returns the shared connection, registering the store so upgrades include it.
 * @param {string} dbName
 * @param {string} storeName
 * @returns {Promise<IDBDatabase>}
 */
const getDBInstance = (dbName, storeName) => {
  if (fallbackMode) {
    return Promise.reject(new Error("Fallback mode: IndexedDB unavailable"));
  }

  knownStoreNames.add(storeName);

  const failed = (error) => {
    dbInstance = null;
    throw error;
  };

  if (!dbInstance) {
    dbInstance = connect(dbName).catch(failed);
    return dbInstance;
  }

  // A hook that mounts later can need a store the live connection lacks; reconnecting
  // closes that live connection before upgrading so the old version cannot block it.
  dbInstance = dbInstance
    .then((db) =>
      db.objectStoreNames.contains(storeName)
        ? db
        : connect(dbName, 1, db)
    )
    .catch(failed);
  return dbInstance;
};

export const useIndexedDB = (
  dbName,
  storeName,
  key,
  initialValue,
  setError = () => {}
) => {
  const [storedValue, setStoredValue] = useState(initialValue);
  const [loading, setLoading] = useState(true);
  const lastSaveRef = useRef(Promise.resolve());

  const handleErrors = useCallback(
    async ({ error, retryFn, delay, attempt }) => {
      const increasedDelay = delay * 2;
      const degrade = (reason) => {
        logIndexedDBEvent("WARNING", "fallback_mode_enabled", { reason });
        fallbackMode = true;
        setStoredValue(initialValue);
        setLoading(false);
        return initialValue;
      };

      if (isSecurityContextError(error)) {
        return degrade("IndexedDB security error, switching to fallback mode.");
      }

      // connect() flips fallbackMode once its own budget is spent. Rethrowing past that
      // point only surfaces an unhandled rejection — the store is already degraded.
      if (fallbackMode) {
        return degrade("IndexedDB unavailable, using in-memory state.");
      }

      const recoverable =
        isDatabaseConnectionLost(error) || isDatabaseDeletedError(error);
      if (!recoverable) {
        throw error;
      }

      // Without a cap, a store whose connection keeps getting invalidated retries forever,
      // re-rendering consumers on every attempt.
      if (attempt >= MAX_CONNECTION_ATTEMPTS) {
        return degrade(
          "IndexedDB operation failed after retries, using in-memory state."
        );
      }

      if (isDatabaseDeletedError(error)) {
        setStoredValue(initialValue);
      }
      dbInstance = null;
      setError(error.message);
      await wait(increasedDelay);
      return retryFn(increasedDelay, attempt + 1);
    },
    [initialValue, setError]
  );

  const saveToDB = useCallback(
    (valueToStore) => {
      if (fallbackMode) {
        logIndexedDBEvent("INFO", "save_skipped_in_fallback_mode");
        return Promise.resolve();
      }

      const saveWithRetry = async (value, delay = 1000, attempt = 1) => {
        try {
          // getDBInstance guarantees the store exists.
          const db = await getDBInstance(dbName, storeName);
          if (!db) {
            throw new Error("Database connection failed.");
          }
          const transaction = db.transaction([storeName], "readwrite");
          const store = transaction.objectStore(storeName);
          store.put(value, key);
          await new Promise((resolve, reject) => {
            transaction.oncomplete = resolve;
            transaction.onerror = () => reject(transaction.error);
          });
        } catch (error) {
          await handleErrors({
            error,
            retryFn: (nextDelay, nextAttempt) =>
              saveWithRetry(value, nextDelay, nextAttempt),
            delay,
            attempt,
          });
        }
      };

      return saveWithRetry(valueToStore).catch((error) => {
        const errorMessage = error?.message || error;
        logIndexedDBEvent("ERROR", "save_failed", { error: errorMessage });
        setError(errorMessage);
        throw error; // critical unexpected error
      });
    },
    [dbName, storeName, key, handleErrors, setError]
  );

  const getValue = useCallback(() => {
    if (fallbackMode) {
      logIndexedDBEvent("INFO", "read_skipped_in_fallback_mode");
      setStoredValue(initialValue);
      setLoading(false);
      return;
    }

    const getWithRetry = async (delay = 1000, attempt = 1) => {
      try {
        setLoading(true);
        // getDBInstance guarantees the store exists.
        const db = await getDBInstance(dbName, storeName);
        if (!db) {
          throw new Error("Database connection failed.");
        }
        const transaction = db.transaction([storeName], "readonly");
        const store = transaction.objectStore(storeName);
        const getRequest = store.get(key);
        const result = await new Promise((resolve, reject) => {
          getRequest.onsuccess = () => resolve(getRequest.result);
          getRequest.onerror = (event) => reject(event.target.error);
        });
        if (result !== undefined) {
          setStoredValue(result);
        } else {
          setStoredValue(initialValue);
          lastSaveRef.current = lastSaveRef.current.then(() =>
            saveToDB(initialValue)
          );
        }
      } catch (error) {
        await handleErrors({
          error,
          retryFn: (nextDelay, nextAttempt) =>
            getWithRetry(nextDelay, nextAttempt),
          delay,
          attempt,
        });
      } finally {
        setLoading(false);
      }
    };

    getWithRetry().catch((error) => {
      const errorMessage = error?.message || error;
      logIndexedDBEvent("ERROR", "read_failed", { error: errorMessage });
      setError(errorMessage);
      throw error; // critical unexpected error
    });
  }, [dbName, storeName, key, initialValue, saveToDB, handleErrors, setError]);

  useEffect(() => {
    getValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = useCallback(
    (newValue) => {
      return new Promise((resolve, reject) => {
        setStoredValue((prevState) => {
          const valueToStore =
            typeof newValue === "function" ? newValue(prevState) : newValue;
          if (!fallbackMode) {
            const savePromise = lastSaveRef.current.then(() =>
              saveToDB(valueToStore)
            );
            lastSaveRef.current = savePromise.catch((e) => {
              logIndexedDBEvent("ERROR", "save_chain_failed", {
                error: getErrorMessage(e),
              });
            });
            savePromise.then(resolve).catch(reject);
          } else {
            resolve();
          }
          return valueToStore;
        });
      });
    },
    [saveToDB]
  );

  const generateStatePropertyFunctions = useCallback(
    (property) => {
      return {
        [`set${property[0].toUpperCase()}${property.slice(1)}`]: (value) => {
          return setValue((prevState) => {
            const valueToSet =
              typeof value === "function" ? value(prevState[property]) : value;
            return { ...prevState, [property]: valueToSet };
          });
        },
      };
    },
    [setValue]
  );

  const statePropertyFunctions = useMemo(() => {
    return Object.keys(storedValue || {}).reduce((acc, property) => {
      return { ...acc, ...generateStatePropertyFunctions(property) };
    }, {});
  }, [storedValue, generateStatePropertyFunctions]);

  return [storedValue, setValue, statePropertyFunctions, loading];
};
